import { X } from '@untitledui/icons'
import { Children, cloneElement, type ReactElement, type ReactNode, useState } from 'react'
import { mergeProps } from 'react-aria'
import { Modal, ModalOverlay } from 'react-aria-components'

import { cx } from '@utils/cx'

/**
 * A drawer component for displaying content in a bottom sheet modal (mobile-first pattern).
 *
 * This component provides a flexible way to show content that slides up from the bottom of the screen.
 * It's designed to work similarly to Popover but for mobile/drawer-style interactions.
 *
 * @example
 * Basic usage with automatic open/close behavior:
 * ```tsx
 * <Drawer>
 *   <Button>Open Drawer</Button>
 *   <DrawerContent>
 *     <p>This is drawer content</p>
 *     <Button>Action</Button>
 *   </DrawerContent>
 * </Drawer>
 * ```
 *
 * @example
 * Controlled usage with manual state management:
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false)
 *
 * <Drawer isOpen={isOpen} onOpenChange={setIsOpen}>
 *   <Button>Toggle Drawer</Button>
 *   <DrawerContent>
 *     <div>Interactive content here</div>
 *   </DrawerContent>
 * </Drawer>
 * ```
 */
export function Drawer({
  children,
  isOpen: controlledIsOpen,
  onOpenChange: externalOnOpenChange,
  isDismissable = true,
}: {
  children: [ReactElement, ReactNode]
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  isDismissable?: boolean
}) {
  const [internalOpen, setInternalOpen] = useState(false)

  // Determine if we're using manual control
  const isManual = controlledIsOpen !== undefined
  const isOpen = controlledIsOpen ?? internalOpen

  const handleOpenChange = (open: boolean) => {
    setInternalOpen(open)
    externalOnOpenChange?.(open)
  }

  // We must separate children to add props to the trigger
  const [triggerElement, contentElement] = Children.toArray(children)

  if (!triggerElement || !contentElement) {
    throw new Error('Drawer requires exactly two children: a trigger element (first child) and a content element (second child)')
  }

  // Merge all conditional props safely
  const triggerWithProps = cloneElement(
    triggerElement as ReactElement,
    mergeProps(
      (triggerElement as ReactElement).props ?? {},
      !isManual ? { onPress: () => handleOpenChange(!isOpen) } : {}
    )
  )

  return (
    <>
      {triggerWithProps}
      <ModalOverlay
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        isDismissable={isDismissable}
        className='backdrop'
      >
        {cloneElement(contentElement as ReactElement<{ onClose?: () => void }>, {
          onClose: () => handleOpenChange(false),
        })}
      </ModalOverlay>
    </>
  )
}

/**
 * Props interface for DrawerContent component.
 */
interface DrawerContentProps {
  /** The content to display inside the drawer */
  children: ReactNode
  /** Additional class name for custom styling */
  className?: string
  /** Callback to close the drawer (automatically passed by Drawer component) */
  onClose?: () => void
}

/**
 * The content container for a drawer.
 *
 * This component wraps the actual content of the drawer and provides default styling
 * including animations, shadows, and padding. It should be used as the second child of the Drawer component.
 */
export function DrawerContent({ children, className, onClose }: DrawerContentProps) {
  return (
    <Modal
      className={cx('drawer-content drawer-transitions relative', className)}
    >
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-fg-primary"
          aria-label="Close drawer"
        >
          <X className="size-6" />
        </button>
      )}
      {children}
    </Modal>
  )
}

